import React, { useCallback, useEffect, useMemo, useRef } from "react";
import * as Sentry from "@sentry/react";

// maps event types to payload types
export interface EventMap {
  focus_chat:
    | {
        source: "chat-about-an-idea-button";
      }
    | {
        /** Help menu → Chat (agentic): PersistentChat persists the greeting assistant message. */
        source: "help_menu_agentic";
      }
    | null;
  focus_chat_input: null;
  focus_history: null;
  send_introductory_chat_messages: null;
  trigger_cta_chat: {
    projectId?: string;
    type: string;
    targetSurface?: "story_bible_outline_summary";
    targetId?: string;
    targetName?: string;
    displayTitle: string;
    userMessage: string;
    cannedResponse: string;
    characterId?: string;
    entryId?: string;
    traitId?: string;
    traitLabel?: string;
    /** generate_outline only: OUTLINE_FRAMWORKS value (e.g. "novel-outline", "custom-outline"). */
    outlineFramework?: string;
    customInstructions?: string;
  };
}

export type EventType = keyof EventMap;

interface BaseEvent<T extends EventType = EventType> {
  id: string;
  type: T;
  payload: EventMap[T];
  timestamp: number;
}

interface EventHandler<T extends EventType = EventType> {
  (event: BaseEvent<T>): void | Promise<void>;
}

interface SudoEventBus {
  emit<T extends EventType>(type: T, payload: EventMap[T]): void;
  subscribe<T extends EventType>(type: T, handler: EventHandler<T>): () => void;
}

class SudoLayoutEventBus implements SudoEventBus {
  private handlers = new Map<EventType, Set<EventHandler>>();
  private eventId = 0;
  private pendingEvents = new Map<string, NodeJS.Timeout>();

  emit<T extends EventType>(type: T, payload: EventMap[T]): void {
    const handlers = this.handlers.get(type);

    const event: BaseEvent<T> = {
      id: `evt_${++this.eventId}`,
      type,
      payload,
      timestamp: Date.now(),
    };

    if (!handlers || handlers.size === 0) {
      Sentry.captureException(new Error(`Event ${type} (${event.id}) has no handlers`));
      return;
    }

    const timeoutId = setTimeout(() => {
      this.pendingEvents.delete(event.id);
      const error = new Error(`Event ${type} (${event.id}) was not handled within 2 seconds`);
      Sentry.captureException(error);
    }, 2000);

    this.pendingEvents.set(event.id, timeoutId);

    Promise.resolve().then(() => {
      const handlerPromises: Promise<void>[] = [];

      handlers.forEach((handler) => {
        try {
          const result = Promise.resolve(handler(event)).catch((error) => {
            Sentry.captureException(error);
          });
          handlerPromises.push(result);
        } catch (error) {
          Sentry.captureException(error);
        }
      });

      Promise.allSettled(handlerPromises).then(() => {
        const timeout = this.pendingEvents.get(event.id);
        if (timeout) {
          clearTimeout(timeout);
          this.pendingEvents.delete(event.id);
        }
      });
    });
  }

  subscribe<T extends EventType>(type: T, handler: EventHandler<T>): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    const handlers = this.handlers.get(type)!;
    handlers.add(handler as EventHandler);

    return () => {
      handlers.delete(handler as EventHandler);
      if (handlers.size === 0) {
        this.handlers.delete(type);
      }
    };
  }
}

const EventBusContext = React.createContext<SudoEventBus | null>(null);

export const SudoLayoutEventProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const eventBus = useMemo(() => new SudoLayoutEventBus(), []);
  return <EventBusContext.Provider value={eventBus}>{children}</EventBusContext.Provider>;
};

export const useSudoLayoutEventBus = (): SudoEventBus => {
  const context = React.useContext(EventBusContext);
  if (!context) {
    throw new Error("useEventBus must be used within EventBusProvider");
  }
  return context;
};

export const useSudoLayoutEventEmitter = () => {
  const eventBus = useSudoLayoutEventBus();
  return useCallback(
    <T extends EventType>(type: T, payload: EventMap[T]) => {
      eventBus.emit(type, payload);
    },
    [eventBus],
  );
};

export const useSudoLayoutEventSubscription = <T extends EventType>(eventType: T, handler: EventHandler<T>, deps: React.DependencyList = []) => {
  const eventBus = useSudoLayoutEventBus();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const stableHandler: EventHandler<T> = (event) => {
      handlerRef.current(event);
    };
    const unsubscribe = eventBus.subscribe(eventType, stableHandler);
    return unsubscribe;
  }, [eventBus, eventType]);
};

export const useOnSudoLayoutEvent = <T extends EventType>(
  eventType: T,
  handler: EventHandler<T>,
  options: {
    enabled?: boolean;
    deps?: React.DependencyList;
  } = {},
) => {
  const { enabled = true, deps = [] } = options;
  useSudoLayoutEventSubscription(eventType, handler, [enabled, ...deps]);
};
